import requester from "api/requester"
import { IDailyScanProgress, IScanMealPayload } from "./mealSlice.types"
import { transformObjectKeysToSnakeCase } from "api/converter"
import { DEFAULT_SUGGESTED_MEALS_PAGE_SIZE } from "config"
import {
  ICreateMealPayload,
  IUpdateMealPayload,
} from "model/food/types/mealPayload.types"
import { MealType } from "model/food/types"
import { IEdamaFood } from "model/food/types/edama.types"
import {
  IScanInitResponse,
  IScanStatusResponse,
} from "model/food/types/AIScan.types"

interface IMealAPI {
  scan: (payload: IScanMealPayload) => Promise<IScanInitResponse>
  getScanStatus: (scanId: string) => Promise<IScanStatusResponse>
  logMeal: (payload: ICreateMealPayload) => Promise<MealType>
  processImage: (file: File) => Promise<any>
  getMealDetails: (id: string) => Promise<MealType>
  getSuggestedMeals: ({
    page,
    signal,
  }: {
    page: number
    signal: AbortSignal
  }) => Promise<MealType[]>
  getDailyScanProgress: () => Promise<IDailyScanProgress>
  updateMeal: (payload: IUpdateMealPayload) => Promise<MealType>
  searchFoodByBarcode: (barcode: string) => Promise<{ foods: IEdamaFood[] }>
}

const getLogMealFormDataPayload = (requestPayload: any) => {
  const formData = new FormData()

  Object.keys(requestPayload).forEach((key) => {
    let data = (requestPayload as any)[key]
    if (data === undefined || data === null || data === "") return

    if (key === "ingredients" || key === "units") {
      formData.append(
        key,
        new Blob([JSON.stringify(data)], {
          type: "application/json",
        }),
      )
    } else {
      formData.append(key, data)
    }
  })

  return formData
}

const mealAPI: IMealAPI = {
  logMeal(payload) {
    const requestPayload = transformObjectKeysToSnakeCase(payload)
    const formData = getLogMealFormDataPayload(requestPayload)
    return requester.post("/meals", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },

  updateMeal({ payload, id }) {
    const requestPayload = transformObjectKeysToSnakeCase(payload)
    const formData = getLogMealFormDataPayload(requestPayload)

    return requester.put(`/meals/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },

  scan(payload) {
    const formData = new FormData()
    if (payload.image) {
      formData.append("image", payload.image)
    } else {
      formData.append("image_id", payload.imageId ?? "")
    }

    return requester.post("/scans", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },

  getScanStatus(scanId) {
    return requester.get(`/scans/${scanId}`)
  },

  processImage(file) {
    const formData = new FormData()
    formData.append("image", file)

    return requester.post("/images/process", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  },

  getMealDetails(id) {
    return requester.get(`/meals/${id}`)
  },

  async getDailyScanProgress() {
    const user = await requester.get("/users/info")

    return {
      remainingScan: user.aiScan?.scanLeft,
      enabled: true,
    }
  },

  getSuggestedMeals({ page, signal }) {
    return requester.get(
      "/meals/suggestions",
      {
        page,
        perPage: DEFAULT_SUGGESTED_MEALS_PAGE_SIZE,
      },
      { signal },
    )
  },

  searchFoodByBarcode(barcode) {
    return requester.get(`/foods/search`, {
      barcode,
    })
  },
}

export default mealAPI
